<template>
    <Popover v-slot="{ open }">
        <div
            :class="[
                'relative',
                {
                    'border rounded-card py-5 px-13 -mx-12 border-input-focus-border ring-2 ring-input-focus-border ':
                        highlightComponent,
                },
            ]"
        >
            <div ref="reference">
                <InputLabel
                    :class="[
                        'mb-2',
                        { 'text-xl font-medium ': highlightComponent },
                    ]"
                    :id="id"
                    :value="label"
                    :description="description"
                    :required="required"
                    :disabled="disabled"
                />

                <TextInput
                    v-if="showInput"
                    :noLabel="true"
                    @keydown.esc="closePopover"
                    @keydown.tab="navigateToResult"
                    v-model="query"
                    :id="id"
                    :label="label"
                    :description="description"
                    :required="required"
                    :error="error"
                    inputClasses="pr-9"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    autocomplete="off"
                    :size="size"
                    ref="inputRef"
                >
                    <template v-slot:absolute>
                        <transition name="fade" mode="out-in">
                            <div
                                v-if="loading"
                                class="absolute right-2 top-1/2 -translate-y-1/2"
                            >
                                <PhCircleNotch
                                    class="size-7 animate-spin text-text-secondary"
                                />
                            </div>
                        </transition>
                    </template>
                </TextInput>
            </div>
            <PopoverButton class="hidden" tabindex="-1" ref="popoverButton" />
            <Teleport v-if="hasBeenInitiated" :to="teleportTarget">
                <transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-if="open">
                        <PopoverWrapper @closed="popoverIsOpen = false">
                            <div
                                ref="floating"
                                :style="[floatingStyles, popoverWidth]"
                                :class="[
                                    smallResults ? 'py-0.5' : 'py-4',
                                    'z-popover',
                                ]"
                            >
                                <div
                                    class="shadow-xl overflow-hidden border scrollbar-gray rounded-popover bg-input-background z-popover divide-y py-4"
                                >
                                    <ModelSearchInputResults
                                        @toggleModelClass="toggleModelClass"
                                        @selectModel="selectModel"
                                        :results="filteredResults"
                                        :loading="loading"
                                        :availableModelClasses="
                                            availableModelClasses
                                        "
                                        :selectedModelClasses="
                                            selectedModelClasses
                                        "
                                        :maxHeight="resultsMaxHeight"
                                        :smallResults="smallResults"
                                        :disabledModels="disabledModels"
                                        ref="results"
                                    />
                                </div>
                            </div>
                        </PopoverWrapper>
                    </div>
                </transition>
            </Teleport>

            <div
                v-if="currentModels.length > 0 && showCurrentModels"
                :class="{ 'pt-3': showInput }"
            >
                <InputError
                    :message="error"
                    :class="{ 'mt-2': size == 'base' }"
                />

                <VueDraggable
                    @start="groupTransitionDisabled = true"
                    :class="[
                        orderable && multiple
                            ? 'w-full md:w-[calc(100%+theme(spacing.12))] md:-ml-12'
                            : 'w-full',
                        'flex flex-col gap-y-3 px-px',
                    ]"
                    v-model="currentModels"
                    @end="modelOrderUpdated"
                    handle=".handle"
                    animation="150"
                    ref="el"
                    :disabled="!(orderable && multiple)"
                >
                    <TransitionGroup
                        :enter-active-class="
                            groupTransitionDisabled
                                ? ''
                                : 'transition-[transform,opacity] duration-500'
                        "
                        :move-class="
                            groupTransitionDisabled
                                ? ''
                                : 'transition-all duration-500'
                        "
                        :enter-from-class="
                            groupTransitionDisabled ? '' : 'scale-0 opacity-0'
                        "
                        :enter-to-class="
                            groupTransitionDisabled
                                ? ''
                                : 'opacity-100 scale-100'
                        "
                        :leave-active-class="
                            groupTransitionDisabled
                                ? ''
                                : 'transition-all duration-500'
                        "
                        :leave-from-class="
                            groupTransitionDisabled
                                ? ''
                                : 'opacity-100 scale-100 max-h-[400px]'
                        "
                        :leave-to-class="
                            groupTransitionDisabled
                                ? ''
                                : 'opacity-0 scale-0 max-h-0'
                        "
                    >
                        <div
                            v-for="model in currentModelsWithTags"
                            :key="`${model.modelClass}-${model.id}`"
                            class="z-5 flex items-center gap-x-2"
                        >
                            <div
                                v-if="orderable && multiple"
                                class="bg-white flex size-10 handle border rounded-full p-1 cursor-row-resize hover:opacity-50 transition-opacity duration-button-hover"
                            >
                                <PhDotsSixVertical weight="bold" />
                            </div>
                            <ModelPreview
                                @remove="removeModel(model)"
                                :showRemoveButton="!disabled"
                                :imageSrc="model?.first_image?.preview_url"
                                :modelClass="model.modelClass"
                                :modelName="model.name"
                                :routeUrl="
                                    $localizedRoute(
                                        model.modelClass + '.show',
                                        model.id
                                    )
                                "
                                :typeId="model.type_id"
                                :fileUrl="model.file"
                                :small="smallResults"
                                :tags="model.tags"
                                ref="previewRef"
                            />
                        </div>
                    </TransitionGroup>
                </VueDraggable>
            </div>
        </div>
    </Popover>
</template>

<script>
import { ref } from 'vue'
import { throttle } from '@/Tools/utilities'
import { VueDraggable } from 'vue-draggable-plus'

import TextInput from '@/Components/FormElements/TextInput.vue'
import InputError from '@/Components/FormElements/InputError.vue'

import { PhCircleNotch, PhDotsSixVertical } from '@phosphor-icons/vue'
import { Popover, PopoverButton } from '@headlessui/vue'
// Use the PopoverPanelWrapper so that we can now when the popover is closed
import PopoverWrapper from '@/Components/Other/PopoverPanelWrapper.vue'
import SearchResult from '@/Components/Search/SearchResult.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import ModelPreview from '@/Components/ModelComponents/ModelPreview.vue'
import InputLabel from '@/Components/FormElements/InputLabel.vue'
import ModelClassTag from '@/Components/ModelComponents/ModelClassTag.vue'
import ModelSearchInputResults from '@/Components/FormElements/ModelSearchInput/ModelSearchInputResults.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import { isArrayLike } from '@/Tools/utilities'

import { useFloating, autoUpdate, offset, flip } from '@floating-ui/vue'

export default {
    components: {
        VueDraggable,
        InputLabel,
        TextInput,
        InputError,
        PhCircleNotch,
        PhDotsSixVertical,
        Popover,
        PopoverWrapper,
        PopoverButton,
        SearchResult,
        ReactiveHeightTransition,
        ModelPreview,
        ModelClassTag,
        ModelSearchInputResults,
        VerticalSlideTransition,
    },

    expose: ['focusMethod', 'highlight'],

    emits: [
        'update:modelValue',
        'currentModelRetrieved',
        'currentModelsUpdated',
    ],

    props: {
        modelValue: {
            /**
             * The modelValue can be either an array or a single value
             * Then it depends if its a number or an object based on the number of availableModelClasses (the classes you can choose from)
             * If we only have one model class to choose from, then the modeValue is a number (Then we dont have to tell the parent what the modelClass is)
             * Otherwise the modelValue is an object with id and modelClass
             */
            type: [Array, Number, String, Object, null],
            default: (props) => (props.multiple ? [] : null),
            validator: (value, props) => {
                if (
                    isArrayLike(value) &&
                    value.some(
                        (model) =>
                            typeof model === 'object' &&
                            !(
                                Object.hasOwn(model, 'model_class') &&
                                Object.hasOwn(model, 'model_id')
                            )
                    )
                ) {
                    console.error(
                        'If the modelValue is an array of objects, then all the objects must have the keys model_class and model_id'
                    )
                    console.error(value)
                    return false
                }
                if (
                    !isArrayLike(value) &&
                    typeof value === 'object' &&
                    !(
                        Object.hasOwn(value, 'model_class') &&
                        Object.hasOwn(value, 'model_id')
                    )
                ) {
                    console.error(
                        'The modelValue if an object must have the keys model_class and model_id'
                    )
                    return false
                }
                return true
            },
        },
        modelClasses: {
            type: Array,
            required: true,
        },
        filters: {
            type: Array,
            required: false,
            default: () => [],
            validator: (value, props) => {
                return value.every((filter) => {
                    return (
                        Object.hasOwn(filter, 'model_class') &&
                        Object.hasOwn(filter, 'column') &&
                        Object.hasOwn(filter, 'operator') &&
                        Object.hasOwn(filter, 'value')
                    )
                })
            },
        },
        label: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        orderable: {
            type: Boolean,
            default: false,
            validator: (value, props) => {
                if (value == true && props.multiple == false) {
                    console.error(
                        'The orderable prop can only be true if the multiple prop is true'
                    )
                    return false
                }
                return true
            },
        },
        error: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            required: true,
        },
        size: {
            type: String,
        },
        showCurrentModels: {
            type: Boolean,
            default: true,
        },
        teleportTarget: {
            type: String,
            default: 'body',
        },
        resultsMaxHeight: {
            type: String,
            required: false,
        },
        smallResults: {
            type: Boolean,
            default: false,
        },
        popoverWidthMultiplier: {
            type: Number,
            default: 1,
        },
        alignment: {
            type: String,
            default: 'center',
            validator: (value) => ['left', 'center'].includes(value),
        },
        disabledModels: {
            type: Array,
            required: false,
        },
        ignoreAccessScope: {
            type: Boolean,
            default: false,
        },
        modelTags: {
            type: Array,
            required: false,
            default: () => [],
            validator: (value, props) => {
                return value.every((model) => {
                    let valid =
                        value instanceof Object && Object.hasOwn(model, 'tags')
                    // If we only have one model class, we dont need to specify the model_class for the tag
                    if (props.modelClasses.length == 1) {
                        valid = valid && Object.hasOwn(model, 'id')
                    } else {
                        valid =
                            valid &&
                            Object.hasOwn(model, 'model_class') &&
                            Object.hasOwn(model, 'model_id')
                    }
                    return valid
                })
            },
        },
        // Models that should not be shown as search results
        ignoredModels: {
            type: Array,
            default: () => [],
        },
    },

    setup(props) {
        const reference = ref(null)
        const floating = ref(null)

        const { floatingStyles, placement, middlewareData } = useFloating(
            reference,
            floating,
            {
                placement:
                    props.alignment == 'center' ? 'bottom' : 'bottom-start',
                middleware: [
                    flip({
                        fallbackPlacements:
                            props.alignment == 'center'
                                ? ['bottom', 'top']
                                : ['bottom-start', 'top-start'],
                        fallbackStrategy: 'initialPlacement',
                        crossAxis: false, // Prevent cross-axis flipping
                        padding: 70,
                    }),
                    offset({
                        mainAxis: 5,
                    }),
                ],
                whileElementsMounted: autoUpdate,
            }
        )

        return {
            placement,
            reference,
            floating,
            floatingStyles,
            middlewareData,
        }
    },

    data() {
        return {
            query: '',
            loading: false,
            selectedModelClasses: [],
            popoverWidth: 'width: 100%',
            popoverIsOpen: false,
            results: [],
            currentModels: [],
            highlightComponent: false,
            groupTransitionDisabled: false,
            hasBeenInitiated: false,
        }
    },

    created() {
        this.throttledSearch = throttle(
            () => {
                this.searchModels()
            },
            300,
            true
        )

        this.throttledFindCurrentModels = throttle(
            () => {
                this.findCurrentModels()
            },
            300,
            true
        )

        this.selectedModelClasses = [...this.availableModelClasses]

        /**
         * This timeout is to ensure that any parents like a model teleport target has been initialized
         * This is because the teleport target might not be ready when the component is created
         * This should not be an issue since no user should be able to fill the input fast enough to be limited by this
         */
        setTimeout(() => {
            this.hasBeenInitiated = true
        }, 500)
    },

    mounted() {
        this.findCurrentModels()
        this.resizeObserver = new ResizeObserver(this.updatePopoverWidth)
        this.resizeObserver.observe(this.$refs.reference)
    },

    beforeUnmount() {
        if (this.resizeObserver && this.$refs.reference) {
            this.resizeObserver.disconnect()
        }
    },

    computed: {
        filteredResults() {
            return this.results.filter(
                (result) =>
                    !this.ignoredModels.some((ignoredModel) => {
                        return (
                            ignoredModel.model_id == result.id &&
                            ignoredModel.model_class == result.modelClass
                        )
                    })
            )
        },

        availableModelClasses() {
            return this.modelClasses.filter((modelClass) =>
                this.$getAvailableModels().includes(modelClass)
            )
        },

        currentModelsWithTags() {
            return this.currentModels.map((model) => ({
                ...model,
                tags: this.modelTags.find((tag) => {
                    if (this.modelClasses.length == 1) {
                        return tag.id == model.id
                    } else {
                        return (
                            tag.model_id == model.id &&
                            tag.model_class == model.modelClass
                        )
                    }
                })?.tags,
            }))
        },

        showInput() {
            return this.multiple || this.currentModels.length == 0
        },
    },

    watch: {
        query() {
            if (this.query.length >= 2) {
                this.throttledSearch()
            } else {
                this.results = []
            }
        },

        popoverIsOpen() {
            if (!this.popoverIsOpen) {
                this.query = ''
                this.results = []
            }
        },

        modelValue() {
            this.throttledFindCurrentModels()
        },

        selectedModelClasses() {
            if (this.popoverIsOpen) {
                this.throttledSearch()
            }
        },

        currentModels() {
            if (this.multiple) {
                this.$emit('currentModelsUpdated', this.currentModels)
            } else {
                this.$emit(
                    'currentModelRetrieved',
                    this.currentModels.length ? this.currentModels[0] : null
                )
            }
        },
    },

    methods: {
        selectModel(model) {
            if (!this.isAlreadyAdded(model)) {
                // If multiple, then we add the model to the array
                if (this.multiple) {
                    // If there is only one model class, then we emit an array of ids
                    if (this.availableModelClasses.length == 1) {
                        this.$emit('update:modelValue', [
                            ...this.modelValue,
                            model.id,
                        ])
                        // If there is more than one model class, then we emit an array of objects containing id and modelClass
                    } else {
                        this.$emit('update:modelValue', [
                            ...this.modelValue,
                            {
                                model_id: model.id,
                                model_class: model.modelClass,
                            },
                        ])
                    }
                } else {
                    // If there is only one model class, then we emit the id
                    if (this.availableModelClasses.length == 1) {
                        this.$emit('update:modelValue', model.id)
                    } else {
                        // If there is more than one model class, then we emit an object containing id and modelClass
                        this.$emit('update:modelValue', {
                            model_id: model.id,
                            model_class: model.modelClass,
                        })
                    }
                }
            }

            this.closePopover()

            setTimeout(() => {
                if (this.showInput) {
                    this.$refs.inputRef.focusMethod()
                } else if (this.$refs.previewRef && this.$refs.previewRef[0]) {
                    this.$refs.previewRef[0].focusDeleteButton()
                }
            }, 400)
        },

        searchModels() {
            this.loading = true
            axios
                .get(route('api.basemodels.search'), {
                    params: {
                        query: this.query,
                        model_classes: this.selectedModelClasses,
                        ignore_access_scope: this.ignoreAccessScope ? 1 : 0,
                        filters: this.filters,
                    },
                })
                .then(({ data }) => {
                    this.results = data
                    this.loading = false
                    this.openPopover()
                })
                .catch((error) => {
                    console.error(error)
                })
        },

        /**
         * Retrieves the current model based on the modelValue
         * If multiple, then we need to send an array of objects with model_id and model_class
         * If not multiple, then we send an array with the model_id
         *
         * If we only have one model class to choose from (availableModelClasses) then we have to attach the model_class to the object(s)
         * Otherwise the modelValue is already correctly formatted
         */
        findCurrentModels() {
            let models = []
            // If multiple, then we send an array of objects with model_id and model_class
            if (this.multiple && this.modelValue.length > 0) {
                models = this.modelValue
                // If we only have one model class to choose from, then we have to attach the model_class to the objects
                if (this.availableModelClasses.length == 1) {
                    models = models.map((model_id) => ({
                        model_id: model_id,
                        model_class: this.availableModelClasses[0],
                    }))
                }
            } else if (!this.multiple && this.modelValue) {
                // If we only have one model class to choose from, then we have to attach the model_class to the object
                if (this.availableModelClasses.length == 1) {
                    models = [
                        {
                            model_id: this.modelValue,
                            model_class: this.availableModelClasses[0],
                        },
                    ]
                } else {
                    models = [this.modelValue]
                }
            }

            if (models.length > 0) {
                axios
                    .get(route('api.find.basemodels'), {
                        params: {
                            models: models,
                            ignore_access_scope: this.ignoreAccessScope ? 1 : 0,
                        },
                    })
                    .then(({ data }) => {
                        this.currentModels = data
                    })
            } else {
                this.currentModels = []
            }
        },

        /**
         * Removes the model from the modelValue
         */
        removeModel(model) {
            if (this.multiple) {
                if (this.availableModelClasses.length == 1) {
                    this.$emit(
                        'update:modelValue',
                        [...this.modelValue].filter((m) => m != model.id)
                    )
                } else {
                    this.$emit(
                        'update:modelValue',
                        [...this.modelValue].filter(
                            (m) => m.model_id != model.id
                        )
                    )
                }
            } else {
                this.$emit('update:modelValue', null)
            }
        },

        modelOrderUpdated() {
            if (this.multiple) {
                // If there is only one model class, then we emit an array of ids
                if (this.availableModelClasses.length == 1) {
                    this.$emit(
                        'update:modelValue',
                        this.currentModels.map((model) => model.id)
                    )
                    // If there is more than one model class, then we emit an array of objects containing id and modelClass
                } else {
                    this.$emit(
                        'update:modelValue',
                        this.currentModels.map((model) => ({
                            model_id: model.id,
                            model_class: model.modelClass,
                        }))
                    )
                }
            }
        },

        /**
         * When pressing a modelClass tag, it will be the only one selected
         * But if it is already the only selected, then we add all the other ones again
         */
        toggleModelClass(modelClass) {
            if (
                this.selectedModelClasses.includes(modelClass) &&
                this.selectedModelClasses.length == 1
            ) {
                this.selectedModelClasses = this.availableModelClasses
            } else {
                this.selectedModelClasses = [modelClass]
            }
        },

        /**
         * If the user presses tab while in the text input, we focus the first result
         */
        navigateToResult(event) {
            if (this.popoverIsOpen) {
                event.preventDefault()
                event.stopPropagation()

                this.$refs.results.navigateToFirstResult()
            }
        },

        updatePopoverWidth() {
            // + 80 px is to make it a bit wider
            this.popoverWidth =
                'width: ' +
                (this.$refs.reference.offsetWidth *
                    this.popoverWidthMultiplier +
                    80) +
                'px'
        },

        openPopover() {
            if (!this.popoverIsOpen) {
                this.$refs.popoverButton.el.click()
                this.popoverIsOpen = true
            }
        },

        closePopover() {
            if (this.popoverIsOpen) {
                this.$refs.popoverButton.el.click()
                this.popoverIsOpen = false
            }
        },

        isAlreadyAdded(model) {
            let alreadyAdded = false

            const isMultipleAndHasModels =
                this.multiple && this.modelValue.length
            const isNotMultipleAndHasModel = !this.multiple && !!this.modelValue
            const hasOneAvailableModelClass =
                this.availableModelClasses.length == 1

            if (isMultipleAndHasModels || isNotMultipleAndHasModel) {
                if (hasOneAvailableModelClass) {
                    alreadyAdded = this.multiple
                        ? this.modelValue.some(
                              (model_id) => model_id == model.id
                          )
                        : this.modelValue == model.id
                } else {
                    // If we have multiple model classes to choose from, then we have to check if the model is already added
                    // this needs to be done by checking the model_id and model_class
                    alreadyAdded = this.multiple
                        ? this.modelValue.some(
                              (m) =>
                                  m.model_id == model.id &&
                                  m.model_class == model.modelClass
                          )
                        : this.modelValue.model_id == model.id &&
                          this.modelValue.model_class == model.modelClass
                }
            }

            return alreadyAdded
        },

        focusMethod() {
            if (this.showInput) {
                this.$refs.inputRef.focusMethod()
            }
        },

        highlight() {
            this.$refs.reference.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
            this.highlightComponent = true
            setTimeout(() => {
                this.focusMethod()
            }, 300)
        },
    },
}
</script>
